import { LoadingStateModel } from '@/models/loadingState';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useStudyStore } from '@/stores/study';
import type { EntryModel, PostEntry, CaseModel, PostLockUnlockResource } from '@/models';
import EntryService from '@/services/entryService';
import { TableColumnType, type TableColumnModel } from '@/components/common/table/interfaces/TableColumnModel';
import type { PaginatorData } from '@/components/common/interfaces/PaginatorData';
import StudyService from '@/services/studyService';
import { DateTime } from 'luxon';
import { useFormStore } from '@/stores/forms';
import { createRequestToken } from '@/stores/requestToken';
import { pageChangeFrom, sortDirectionFrom } from '@/stores/listControls';
import FormService from '@/services/formService';
import { caseEntryRules, offersAnyForm, studyEntryRules } from '@/utils/caseEntryRules';
import { canAddEntries } from '@/models/grant';
import { createItemRefusals } from '@/stores/itemRefusals';
import type { ItemStatus } from '@/models/itemRefusal';
import { translate } from '@/utils/translate';

export const useEntryListStore = defineStore('entryList', () => {

  const tableColumns = ref<TableColumnModel[]>([
    {
      id: 1,
      header: 'Title',
      key: 'formTitle',
      type: TableColumnType.TEXT_WITH_ICON,
      icon: {
        icon: 'file',
        style: 'far',
        color: '#272a2e'
      },
      sortable: true
    },
    {
      id: 3,
      header: 'Creation Date',
      type: TableColumnType.TEXT,
      key: 'createdAt',
      sortable: true
    },
    {
      id: 4,
      header: 'Last Edited On',
      type: TableColumnType.TEXT,
      key: 'updatedAt',
      sortable: true
    },
    {
      id: 5,
      header: 'Last Edited By',
      type: TableColumnType.USER,
      key: 'creatorNames',
      sortable: true
    },
    {
      id: 6,
      header: '',
      type: TableColumnType.SQUARE_BUTTON_WITH_ICON,
      key: '',
      sortable: false
    }
  ]);

  const selectedCase = ref<CaseModel>();

  const formStore = useFormStore();
  const studyStore = useStudyStore();
  const entryList = ref<EntryModel[]>([]);
  const totalEntriesCount = ref<number>(0);
  const currentPage = ref(1);
  const pageLimit = ref(25);
  const sortField = ref<string | undefined>('createdAt');
  const sortOrder = ref<string | undefined>('DESC');

  const entriesLoader = ref(new LoadingStateModel({ isLoading: false }));

  /**
   * One counter across every read below. `reloadEntries` picks between them by whether a case is
   * open, and on a refresh straight onto a case URL that answer changes while the first read is
   * still out — so the two fill the same list and the newest ask wins it.
   */
  const entriesRequest = createRequestToken();

  const getEntries = async () => {
    const token = entriesRequest.claim();
    entriesLoader.value.startLoading();
    const caseColumnIndex = tableColumns.value.findIndex(column => column.id === 2);
    if (caseColumnIndex > -1) {
      tableColumns.value.splice(caseColumnIndex, 1);
    }
    try {
      let entryResponse;
      if (selectedCase.value?.archivedAt) {
        entryResponse = await EntryService.getCaseArchivedEntries(
            studyStore.selectedStudyId!, selectedCase.value!.identifier);
      } else if (selectedCase.value) {
        entryResponse = await EntryService.getCaseEntries(
            studyStore.selectedStudyId!, selectedCase.value!.identifier, currentPage.value,
            pageLimit.value, sortField.value, sortOrder.value
        );
      }
      if (!entriesRequest.holds(token)) return;
      entryList.value = entryResponse?.entries ?? [];
      totalEntriesCount.value = entryResponse?.entriesCount ?? 0;
      getForms();
    } catch (error) {
      if (!entriesRequest.holds(token)) return;
      entriesLoader.value.setError(error);
    } finally {
      if (entriesRequest.holds(token)) {
        entriesLoader.value.stopLoadingAndFinish();
      }
    }
  };

  // A case's forms read is the study's plus that case's entry rules, and only the second is the
  // case's own; `getEntries` is the one reader that runs once per case opened.
  const getForms = () => {
    formStore.getStudyForms(studyStore.selectedStudyId);
    if (selectedCase.value) {
      getCaseAllowEntry(studyStore.selectedStudyId, selectedCase.value.identifier);
    }
  };

  /**
   * Whether the case `allowEntryCaseId` may add an entry to each form, by form id: the one part of
   * the forms read that is the case's and not the study's. Keyed by that case, so a reader on another
   * case reads nothing from it, and blanked only when the case changes.
   */
  const allowEntryById = ref<Record<string, boolean>>({});
  const allowEntryCaseId = ref<string>();
  const allowEntryLoader = ref(new LoadingStateModel({ isLoading: false }));
  const allowEntryRequest = createRequestToken();
  // `getEntries` asks on every page and sort of an open case, so a second ask for the case already in
  // flight shares that read, as the study's forms read does.
  let allowEntryInFlight: { caseId: string; read: Promise<void> } | undefined;

  const getCaseAllowEntry = (studyId: string | undefined, caseId: string): Promise<void> => {
    // The case's read lists only the forms the reader may view, and refuses one who may view none.
    if (!studyId || formStore.refusesFormsRead(studyId, { forCase: true })) {
      return Promise.resolve();
    }
    if (allowEntryInFlight?.caseId === caseId) {
      return allowEntryInFlight.read;
    }
    const token = allowEntryRequest.claim();
    if (allowEntryCaseId.value !== caseId) {
      allowEntryById.value = {};
      allowEntryCaseId.value = caseId;
    }
    allowEntryLoader.value.startLoading();
    const read = (async () => {
      try {
        const allowEntry = await FormService.getCaseAllowEntry(studyId, caseId);
        if (!allowEntryRequest.holds(token)) return;
        allowEntryById.value = allowEntry;
      } catch (error) {
        if (!allowEntryRequest.holds(token)) return;
        allowEntryLoader.value.setError(error);
      } finally {
        if (allowEntryRequest.holds(token)) {
          allowEntryInFlight = undefined;
          allowEntryLoader.value.stopLoadingAndFinish();
        }
      }
    })();
    allowEntryInFlight = { caseId, read };
    return read;
  };

  /** What the New entry menu may offer against the list on screen, by form id. */
  const newEntryRules = computed(() => {
    const openCase = selectedCase.value;
    if (!openCase) {
      return studyEntryRules(formStore.formList, entryList.value);
    }
    const allowEntry = allowEntryCaseId.value === openCase.identifier ? allowEntryById.value : {};
    return caseEntryRules(formStore.formList, allowEntry, entryList.value);
  });

  /**
   * Whether the New entry button is drawn: in the section's chrome, and in this list's empty state
   * after the sentence that leads to it, which reads this rather than a copy of it (ENG-10306).
   *
   * Three things have to hold. The list takes entries here: a study that does not group entries into
   * cases has only the study's list, and one that does takes a new entry in an open case that is
   * neither locked nor archived. The reader's grant in the selected study adds entries at all, which
   * across a switch it says nothing about until it names that study. And the menu has a form to
   * offer, unless the study has no forms, which keeps the button as it always has.
   */
  const offersNewEntry = computed(() => {
    const openCase = selectedCase.value;
    const takesEntries = studyStore.selectedStudy?.longitudinal !== true ||
      (openCase !== undefined && !openCase.archivedAt && openCase.lockedAt == undefined);
    const grant = studyStore.grantFor(studyStore.selectedStudyId);
    return takesEntries && grant !== undefined && canAddEntries(grant) &&
      offersAnyForm(formStore.formList, newEntryRules.value);
  });

  const getStudyEntries = async () => {
    const token = entriesRequest.claim();
    entriesLoader.value.startLoading();
    if (studyStore.selectedStudy?.longitudinal === true) {
      const caseColumnIndex = tableColumns.value.findIndex(column => column.id === 2);
      if (caseColumnIndex === -1) {
        const caseTableColumn = {
          id: 2,
          header: 'Case',
          type: TableColumnType.SELECT,
          key: 'caseLabel',
          sortable: false
        };
        tableColumns.value.splice(1, 0, caseTableColumn);
      }
    } else {
      const caseColumnIndex = tableColumns.value.findIndex(column => column.id === 2);
      if (caseColumnIndex > -1) {
        tableColumns.value.splice(caseColumnIndex, 1);
      }
    }
    try {
      // Together, not one after the other. The forms build this list's columns and the entries fill
      // its rows, and neither read needs anything the other returns — they were serial only because
      // the case read that used to sit between them made the whole arrival a chain. `getStudyForms`
      // keeps its own failure, so this settles on the entry read alone.
      const [, entryResponse] = await Promise.all([
        formStore.formList.length === 0 ? formStore.getStudyForms(studyStore.selectedStudyId) : undefined,
        EntryService.getStudyEntries(
          studyStore.selectedStudyId!, currentPage.value,
          pageLimit.value, sortField.value, sortOrder.value
        )
      ]);
      if (!entriesRequest.holds(token)) return;
      entryList.value = entryResponse.entries;
      totalEntriesCount.value = entryResponse.entriesCount;
    } catch (error) {
      if (!entriesRequest.holds(token)) return;
      entriesLoader.value.setError(error);
    } finally {
      if (entriesRequest.holds(token)) {
        entriesLoader.value.stopLoadingAndFinish();
      }
    }
  };

  /**
   * The read this list wants: one case's entries when a case is open, the study's otherwise.
   *
   * Written three ways and read off a localStorage key before the data routes split, which meant a
   * study whose remembered view was `cases` or `pipeline` asked for a case's entries with no case
   * selected — `getEntries` returns nothing at all in that state, so the list rendered empty.
   */
  const reloadEntries = () => (selectedCase.value ? getEntries() : getStudyEntries());

  const onPageChange = (pageData: PaginatorData) => {
    const change = pageChangeFrom(pageData, currentPage.value, pageLimit.value);
    if (!change) {
      return;
    }
    currentPage.value = change.page;
    pageLimit.value = change.limit;
    reloadEntries();
  };

  const setSort = (sortBy?: string) => {
    if (sortBy != sortField.value) {
      sortField.value = sortBy;
      reloadEntries();
    }
  };

  const setSortOrder = (order?: number) => {
    const direction = sortDirectionFrom(order, sortOrder.value);
    if (!direction) {
      return;
    }
    sortOrder.value = direction;
    reloadEntries();
  };

  const entryActionLoader = ref(new LoadingStateModel({ isLoading: false }));
  const itemRefusals = createItemRefusals();

  /** Resolves true when the server refused the change, which then changed nothing. */
  const lockUnlockEntries = async (selectedEntries: EntryModel[], status: ItemStatus): Promise<boolean> => {
    entryActionLoader.value.startLoading();
    itemRefusals.clear();
    let refused = false;
    const postPayload: PostLockUnlockResource = {
      responses: selectedEntries.map(entryItem => ({
        identifier: entryItem.identifier,
        revisionIdentifier: entryItem.identifier
      })),
      status
    };
    try {
      if (status === 'locked' || status === 'unlocked') {
        await StudyService.lockUnlockResources(studyStore.selectedStudyId!, postPayload);
        entryActionLoader.value.setSuccess(translate(status === 'locked' ? 'entries_lock_success' : 'entries_unlock_success'));
      } else {
        await StudyService.archiveResources(studyStore.selectedStudyId!, postPayload);
        entryActionLoader.value.setSuccess(translate('entries_archive_success'));
      }
      reloadEntries();
    } catch (error) {
      refused = itemRefusals.fail(error, entryActionLoader.value, { status, rows: selectedEntries, of: 'entries' });
    } finally {
      entryActionLoader.value.stopLoadingAndFinish();
    }
    return refused;
  };

  const updateEntryCase = async (entryId: string, caseId?: string) => {
    entriesLoader.value.startLoading();
    try {
      if (formStore.formList.length > 0) {
        const payload: PostEntry = {
          caseUri: caseId ? `case/${caseId}` : null,
          revisionDate: DateTime.local().toSeconds()
        };
        await EntryService.updateEntry(entryId, payload);
        getStudyEntries();
      }
    } catch (error) {
      entriesLoader.value.setError(error);
    } finally {
      entriesLoader.value.stopLoadingAndFinish();
    }
  };

  const resetData = () => {
    selectedCase.value = undefined;
    entryList.value = [];
  };

  const resetEntryList = (caseModel?: CaseModel) => {
    entryList.value = [];
    selectedCase.value = caseModel;
    currentPage.value = 1;
    pageLimit.value = 25;
    sortField.value = 'createdAt';
  };

  return {
    getEntries,
    getStudyEntries,
    reloadEntries,
    onPageChange,
    lockUnlockEntries,
    refusalFor: itemRefusals.reasonFor,
    refusedSelection: itemRefusals.refusedSelection,
    clearRefusals: itemRefusals.clear,
    updateEntryCase,
    resetData,
    resetEntryList,
    setSort,
    setSortOrder,
    getCaseAllowEntry,
    allowEntryById,
    allowEntryCaseId,
    newEntryRules,
    offersNewEntry,
    currentPage,
    tableColumns,
    selectedCase,
    entriesLoader,
    entryActionLoader,
    entryList,
    totalEntriesCount
  };
});
